<script setup lang="ts">
  import { computed } from 'vue'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import { useUserStore } from '@/shared/store'
  import FlowCardQC from '@/shared/components/FlowCardQC/FlowCardQC.vue'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import type { TravelerInformation as ApiTravelerInformation } from '@/shared/types/api.types'
  import { FlowDataProtectReminderQC } from '@/entities/Flow/components/FlowDataProtectReminderQC'
  import { getTravelersInformation } from './FlowInsuredTravelersWidgetQC.utils'
  import {
    ADD_EVENT_NAME,
    DELETE_EVENT_NAME,
    EDIT_EVENT_NAME,
    HOLDER_ID,
    PROCEED_EVENT_NAME,
    TRAVELERS_LIMIT_OUTBOUND,
  } from './FlowInsuredTravelersWidgetQC.constants'
  import TravelerCard from './components/TravelerCard/TravelerCard.vue'
  import { TravelDirection } from '@/shared/types/common.types'
  import { useWinterSport } from '@/shared/composables'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import {
    useGetInsuredTravelerOptions,
    type TravelerOption,
  } from '@/shared/composables/useGerInsuredTravelersOptions'
  import { useI18n } from 'vue-i18n'

  interface Props {
    travelers: ApiTravelerInformation[]
    typeOfTravel?: Exclude<TravelDirection, TravelDirection.UNKNOWN>
    withBackButton?: boolean
  }

  interface Emits {
    (e: typeof PROCEED_EVENT_NAME): void
    (e: typeof EDIT_EVENT_NAME, id: string): void
    (e: typeof DELETE_EVENT_NAME, id: string): void
    (e: typeof ADD_EVENT_NAME): void
    (e: 'back'): void
  }

  const props = withDefaults(defineProps<Props>(), {
    typeOfTravel: TravelDirection.OUTBOUND,
    withBackButton: false,
  })

  const emit = defineEmits<Emits>()

  const { locale } = useI18n()
  const { getInsuredTravelerOptions } = useGetInsuredTravelerOptions()
  const userStore = useUserStore()
  const { withWinterSport } = useWinterSport()
  const isHolderOpen = computed(() => props.travelers.length === 0)

  const isOutbound = computed(() => props.typeOfTravel === TravelDirection.OUTBOUND)

  const travelersInformation = computed(() =>
    getTravelersInformation(props.travelers, withWinterSport.value, props.typeOfTravel)
  )
  const limitTravelersOutBound = computed(
    () => isOutbound.value && travelersInformation.value.length + 1 >= TRAVELERS_LIMIT_OUTBOUND
  )
  const holderName = computed(() => `${userStore.firstName} ${userStore.familyName}`)

  const holderInformation = computed((): TravelerOption => {
    const generalInformation: TravelerOption = {
      srNo: HOLDER_ID,
      email: String(userStore.email || ''),
      phone: String(userStore.maskedPhone || ''),
      dateOfBirth: new Date(userStore.dateOfBirth || ''),
      nationalityName: String(userStore.nationality?.nationalityName),
      passportNo: String(userStore.passportNumber || ''),
      gender: String(userStore.gender || ''),
    }

    const winterSportInformation = withWinterSport.value
      ? { withWinterSport: userStore.withWinterSport }
      : {}

    const outboundInformation = {
      emiratesId: String(userStore.emiratesId || ''),
      civilIdExpiryDate: new Date(userStore.civilIdExpiryDate || ''),
      ...winterSportInformation,
    }

    if (isOutbound.value) {
      return {
        ...generalInformation,
        ...outboundInformation,
      }
    } else {
      return generalInformation
    }
  })

  const onHolderEdit = () => {
    gaService.sendInsuredTravelersEdit(props.typeOfTravel)
    emit(EDIT_EVENT_NAME, HOLDER_ID)
  }

  const onTravelerDelete = (id: string) => {
    gaService.sendInsuredTravelersDelete(props.typeOfTravel)
    emit(DELETE_EVENT_NAME, id)
  }

  const onTravelerEdit = (id: string) => {
    gaService.sendInsuredTravelersEdit(props.typeOfTravel)
    emit(EDIT_EVENT_NAME, id)
  }

  const onAddTraveler = () => {
    gaService.sendInsuredTravelersAdd(props.typeOfTravel)
    emit(ADD_EVENT_NAME)
  }

  const gaService = useGoogleAnalytics()

  const onProceed = () => {
    gaService.sendInsuredTravelersProceed(
      props.typeOfTravel,
      travelersInformation.value.length + 1,
      userStore.destination?.map((country) => country?.id || '')
    )
    emit(PROCEED_EVENT_NAME)
  }
</script>

<template>
  <FlowCardQC
    :title="$t('travellers_insured')"
    :with-back-button="withBackButton"
    @back="emit('back')"
  >
    <div class="travelers-qc">
      <div class="travelers-qc__cards">
        <TravelerCard
          :name="holderName"
          :information="getInsuredTravelerOptions(holderInformation, props.typeOfTravel, false)"
          :sr-no="holderInformation.srNo"
          :opened="isHolderOpen"
          @edit="onHolderEdit"
        />
        <TravelerCard
          v-for="traveler in travelersInformation"
          :key="traveler.id"
          :name="traveler.name"
          :sr-no="traveler.id"
          :information="getInsuredTravelerOptions(traveler.information, props.typeOfTravel, false)"
          @delete="onTravelerDelete(traveler.id)"
          @edit="onTravelerEdit(traveler.id)"
        />
      </div>
    </div>

    <template #button>
      <WrapperQC direction="column" gap="var(--spacing-6)">
        <ButtonQC
          wide
          icon="add-circle"
          variant="secondary"
          :disabled="limitTravelersOutBound"
          data-test="add-traveler-button"
          @click="onAddTraveler"
        >
          {{ $t('inbound.travelers.add_button') }}
        </ButtonQC>

        <ButtonQC
          wide
          :icon-after-content="`arrow-narrow-${locale === 'en' ? 'right' : 'left'}`"
          data-test="proceed-button"
          @click="onProceed"
        >
          {{ $t('travel.common.continue.button') }}
        </ButtonQC>
      </WrapperQC>
    </template>

    <template #after>
      <FlowDataProtectReminderQC />
    </template>
  </FlowCardQC>
</template>
